import { useMemo, useState } from 'react';
import { MDSButton, MDSDropdown } from '@marqvision/mds-v2';
import { useGetAllOIQuery } from '~/apis/common';
import { OFFICIAL_IMAGE_FILTER_LIMIT } from '~/apis/common/filter';
import { useGetFilterParams, usePathParams } from '~/components/Filters/@hooks';
import { ResponsiveProps } from '~/components/Responsive/@types';
import { useLogger } from '~/hooks';
import { MarqCommonEnumsSortedOrderEnum } from '~/swagger/data-contracts';
import { getListingTabGroup, mergePages } from '~/utils';

export const CopyrightImageFilter = (props: ResponsiveProps) => {
  const { size = 'medium' } = props;
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { page, listingStatus } = usePathParams();
  const { amplitudeLogging } = useLogger();
  const [keywordInFilter, setKeywordInFilter] = useState('');

  const oiQuery = useGetAllOIQuery({
    ...requestFilterParams,
    size: OFFICIAL_IMAGE_FILTER_LIMIT,
    search_after: [],
    in_search_keyword_filter: keywordInFilter,
    sorted_order: MarqCommonEnumsSortedOrderEnum.Asc,
  });

  const list = useMemo(() => {
    return mergePages(oiQuery?.data?.pages)?.list || [];
  }, [oiQuery.data]);

  const selectedValues = useMemo(() => {
    if (!params.ois) return [];
    const ois = Array.isArray(params.ois) ? params.ois : [params.ois];
    return ois.includes(-1) ? list.map((item) => item.value) : ois;
  }, [params.ois, list]);

  const handleSelect = (value: (string | number)[]) => {
    if (value.length === 0) {
      removeParams(['ois']);
    } else {
      amplitudeLogging('applied filter', {
        Type: 'ois',
        Channel: page,
        Page: getListingTabGroup(listingStatus),
      });

      const isSelectAll = value.length === list.length;
      if (isSelectAll) {
        setParams({ ois: [-1] }, { replace: true });
      } else {
        setParams({ ois: value as number[] }, { replace: true });
      }
    }
  };

  const handleSearch = (value: string) => {
    if (value.trim() === '') {
      setKeywordInFilter('');
    } else {
      setKeywordInFilter(value);
    }
  };

  const handleFetchNextPage = () => {
    if (!oiQuery.isFetching && !oiQuery.isFetchingNextPage) {
      oiQuery.fetchNextPage();
    }
  };

  const isInfiniteScrollDropdown = useMemo(
    () => (oiQuery.data?.pages.flatMap((page) => page.list).length || 0) > 0,
    [oiQuery.data?.pages]
  );

  const handleAllClick = () => {
    if (params.ois) {
      removeParams(['ois']);
    } else {
      setParams({ ois: [-1] }, { replace: true });
    }
  };

  const variant = params.ois ? 'fill' : 'tint';

  return (
    <div>
      <MDSButton
        variant={variant}
        color="bluegray"
        onClick={handleAllClick}
        isDisabled={list.length === 0}
        flat="right"
        isLoading={oiQuery.isLoading}
      >
        Copyrighted image used
      </MDSButton>
      <MDSDropdown
        width={500}
        isLoading={oiQuery.isLoading}
        modules={[
          'hide-select-all',
          {
            type: 'search',
            onChange: handleSearch,
          },
          {
            type: 'filter-button',
            flat: 'left',
            size,
          },
          {
            type: 'infinite',
            total: list.length,
            hasNextPage: oiQuery.hasNextPage,
            onScrollBottom: () => {
              if (isInfiniteScrollDropdown && !oiQuery.isFetchingNextPage) {
                handleFetchNextPage();
              }
            },
          },
        ]}
        list={list}
        value={selectedValues}
        onChange={handleSelect}
        isFoldAll
      />
    </div>
  );
};
